import { type FC } from "react";
import { type ActiveStorageBlob } from "react-activestorage";

import { faFileLines } from "@fortawesome/pro-duotone-svg-icons/faFileLines";
import { faFilePen } from "@fortawesome/pro-duotone-svg-icons/faFilePen";

import {
  DOCX_EXTENSION,
  DOCX_MIME_TYPE,
  PDF_EXTENSION,
  PDF_MIME_TYPE,
} from "@application/utilities/file";

import { FileManager } from "@styled/file_manager";
import { FileQueue } from "@styled/file_queue";

import { ChoiceCard } from "./choice_card";
import { PriorDrop } from "./prior_drop";
import {
  type RecordDoc,
  RecordFilePill,
  RecordFilePillStack,
  ZoneSection,
} from "./records_file_pills";
import { renderFileQueueItem } from "./shared_card_utils";

export type FollowUpPriorInputKind = "source_pdfs" | "record_review";

const PDF_ACCEPT = [PDF_EXTENSION, PDF_MIME_TYPE].join(",");
const PDF_DOCX_ACCEPT = [
  PDF_EXTENSION,
  PDF_MIME_TYPE,
  DOCX_EXTENSION,
  DOCX_MIME_TYPE,
].join(",");

// Per D10: role labels shown in the .fp-pages slot once both zones are
// filled so the user can tell prior input from new records without the
// section headers.
const ROLE_LABEL_SOURCE_PDFS = "prior input · original medical records";
const ROLE_LABEL_RECORD_REVIEW = "prior input · prior review document";
const ROLE_LABEL_NEW_RECORDS = "new records";

type SaveHandler = (blob: ActiveStorageBlob) => Promise<void>;

/**
 * Multi-zone upload stage for the follow-up flow per design handoff D10.
 *
 * Each zone owns its own `FileManager` so drops on zone 2 always route to
 * `onUploadSourceDocument` regardless of zone 1's state, and the two zones
 * process in parallel. The component is agnostic to the parent RSB chrome —
 * it inspects its own props to decide drop-vs-pill per zone.
 */
export const FollowUpUploadStage: FC<{
  projectID: string;
  priorReviewDoc: RecordDoc | null;
  baselineSourceDocs: RecordDoc[];
  additionalSourceDocs: RecordDoc[];
  priorInputKind: FollowUpPriorInputKind;
  onPriorInputKindChange: (value: FollowUpPriorInputKind) => void;
  onUploadPriorReview: SaveHandler;
  onUploadPriorRecords: SaveHandler;
  onUploadSourceDocument: SaveHandler;
}> = ({
  projectID,
  priorReviewDoc,
  baselineSourceDocs,
  additionalSourceDocs,
  priorInputKind,
  onPriorInputKindChange,
  onUploadPriorReview,
  onUploadPriorRecords,
  onUploadSourceDocument,
}) => {
  const zone1HasDoc = priorReviewDoc !== null || baselineSourceDocs.length > 0;
  // Once zone 1 has a doc, the input-kind is determined by what's there —
  // the choice cards stop being meaningful and disappear. While zone 1 is
  // empty, the choice cards are the user's way to pick.
  const isSourceChoice = zone1HasDoc
    ? priorReviewDoc === null
    : priorInputKind === "source_pdfs";
  // Zone 1's source-PDF path uses `intent: PriorRecords`; the server marks
  // the document `baseline: true`. Zone 2 uses `intent: NewRecords`, which
  // creates a non-baseline document.
  const zone1SaveHandler = isSourceChoice
    ? onUploadPriorRecords
    : onUploadPriorReview;

  // Per D10: once both zones have files (C1-c / PL-5), drop the section
  // labels and render file pills directly with role labels in .fp-pages.
  const bothZonesFilled = zone1HasDoc && additionalSourceDocs.length > 0;
  if (bothZonesFilled) {
    return (
      <div>
        {priorReviewDoc ? (
          <RecordFilePill
            doc={priorReviewDoc}
            projectID={projectID}
            tone="upload"
            pagesLabel={ROLE_LABEL_RECORD_REVIEW}
          />
        ) : (
          baselineSourceDocs.map((doc, idx) => (
            <div key={doc.id} className={idx > 0 ? "mt-2" : undefined}>
              <RecordFilePill
                doc={doc}
                projectID={projectID}
                tone="upload"
                pagesLabel={ROLE_LABEL_SOURCE_PDFS}
              />
            </div>
          ))
        )}
        {additionalSourceDocs.map((doc) => (
          <div key={doc.id} className="mt-2">
            <RecordFilePill
              doc={doc}
              projectID={projectID}
              tone="upload"
              pagesLabel={ROLE_LABEL_NEW_RECORDS}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Choice cards only while nothing has been uploaded yet (C1-a). */}
      {!zone1HasDoc && (
        <div
          role="radiogroup"
          aria-label="Prior input source"
          className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2"
        >
          <ChoiceCard
            icon={faFileLines}
            title="Original Medical Records"
            description="The original medical records from your prior evaluation. Best for accurate comparison."
            recommended
            selected={priorInputKind === "source_pdfs"}
            onClick={() => onPriorInputKindChange("source_pdfs")}
          />
          <ChoiceCard
            icon={faFilePen}
            title="Prior Review Document"
            description="Your previous record review summary (.docx or PDF). Use when original records aren't available."
            selected={priorInputKind === "record_review"}
            onClick={() => onPriorInputKindChange("record_review")}
          />
        </div>
      )}

      <ZoneSection tone="upload" label="1. Prior Records">
        {priorReviewDoc ? (
          <RecordFilePill
            doc={priorReviewDoc}
            projectID={projectID}
            tone="upload"
          />
        ) : baselineSourceDocs.length > 0 ? (
          /*
            Zone 1 multi-baseline: every priorRecords doc is `Document#baseline=
            true` (set server-side from `intent: PRIOR_RECORDS`). The drop
            zone transitions out once any priors land — symmetric with Zone 2.
            Multi-select on the initial drop covers the multi-PDF case;
            users add more later via Add Records once processing completes.
          */
          <RecordFilePillStack
            docs={baselineSourceDocs}
            projectID={projectID}
            tone="upload"
          />
        ) : (
          /*
            First-upload state. Zone 1 supports multi-file when the user
            picks Source PDFs (multi-PDF priors are common). The server
            decides which document(s) become baseline via the
            RunDocumentIntent.PriorRecords arg — the frontend never reads
            project state for that decision (CLAUDE.md: no business logic
            in the frontend).
          */
          <FileManager save={zone1SaveHandler}>
            <PriorDrop
              icon={isSourceChoice ? faFileLines : faFilePen}
              title={
                isSourceChoice
                  ? "Drop prior records here"
                  : "Drop your prior review document here"
              }
              subtitle={
                isSourceChoice
                  ? "The original medical records from the prior evaluation"
                  : "The summary letters from your last review"
              }
              formats={
                isSourceChoice
                  ? "PDF · Multiple files OK"
                  : "DOCX · PDF · Single file"
              }
              accept={isSourceChoice ? PDF_ACCEPT : PDF_DOCX_ACCEPT}
              multiple={isSourceChoice}
            />
            <FileQueue render={renderFileQueueItem} />
          </FileManager>
        )}
      </ZoneSection>

      <ZoneSection tone="upload" label="2. New Records">
        {additionalSourceDocs.length > 0 ? (
          <RecordFilePillStack
            docs={additionalSourceDocs}
            projectID={projectID}
            tone="upload"
          />
        ) : (
          <FileManager save={onUploadSourceDocument}>
            <PriorDrop
              icon={faFileLines}
              title="Drop new records here"
              subtitle="Records received since the prior evaluation"
              formats="PDF"
              accept={PDF_ACCEPT}
              multiple
            />
            <FileQueue render={renderFileQueueItem} />
          </FileManager>
        )}
      </ZoneSection>
    </div>
  );
};
